import { useEffect, useState } from 'react';
import { LANDING_URL } from '@segurodeceso/constants/url';
import { AgentT } from '@segurodeceso/core/domain/agent';
import getUf from '@services/getUf';
import { useAgentStore, useCurrencyStore, useLeadStore } from '@stores';
import { quoterPageView } from '@utils/datalayer';
import router from 'next/router';

import useFlow from './useFlow';

interface UsePageQuoterI {
  agent?: AgentT;
  assistedSale?: boolean;
  referral?: string;
}

const usePageQuoter = ({ agent, assistedSale, referral }: UsePageQuoterI) => {
  const setDefaultLead = useLeadStore((state) => state.setDefaultLead);
  const setAgent = useAgentStore((state) => state.setAgent);
  const {
    setCompletedStep,
    setHasFamilyMembers,
    isLoading: isFlowLoading,
    resetFlow,
  } = useFlow({ currentStep: 1 });

  const setNoInsuredMembers = (noInsuredMembers: number) => {
    useLeadStore.setState((state) => {
      return {
        lead: {
          ...state.lead,
          insurance: {
            ...state.lead.insurance,
            noInsuredMembers: noInsuredMembers,
          },
          dependantsInformation: [
            ...state.lead.dependantsInformation.slice(0, noInsuredMembers - 1),
          ],
          referral,
        },
      };
    });
    resetFlow();
  };
  const noInsuredMembers = useLeadStore((state) => state.lead.insurance.noInsuredMembers);
  const setSelectedPlan = useLeadStore((state) => state.setPlan);
  const selectedPlan = useLeadStore((state) => state.lead.insurance.planId);
  const setReferral = useLeadStore((state) => state.setReferral);

  const [loading, setLoading] = useState(false);
  const [preLoading, setPreLoading] = useState(true);
  const [dataIsValid, setDataIsValid] = useState(false);

  const handleBack = () => {
    router.push(LANDING_URL);
  };

  const handleContinue = async () => {
    setLoading(true);
    await setCompletedStep({ 1: true });
    await setHasFamilyMembers(noInsuredMembers > 1);
    router.push('/personal');
  };

  const validateData = (selectedPlan: number, noInsuredMembers: number) => {
    if (!selectedPlan || selectedPlan === 0 || !noInsuredMembers || noInsuredMembers === 0) {
      setDataIsValid(false);
      return;
    }
    setDataIsValid(true);
  };

  useEffect(() => {
    validateData(Number(selectedPlan), noInsuredMembers);
  }, [selectedPlan, noInsuredMembers]);

  useEffect(() => {
    let isLeadStoreInitialized = useLeadStore.getState().isInitialized;
    let isCurrencyStoreInitialized = useCurrencyStore.getState().isInitialized;

    const checkInitialization = () => {
      if (isLeadStoreInitialized && isCurrencyStoreInitialized) {
        setPreLoading(false);
      }
    };

    if (!isLeadStoreInitialized) {
      setDefaultLead();
      useLeadStore.setState({ isInitialized: true });
      isLeadStoreInitialized = true;
      checkInitialization();
    }

    if (!isCurrencyStoreInitialized) {
      const signal = new AbortController().signal;
      getUf(signal)
        .then((uf: number) => {
          useCurrencyStore.setState({
            currency: {
              UFValue: uf,
              UFDate: Date.now(),
            },
          });
          useCurrencyStore.setState({ isInitialized: true });
          isCurrencyStoreInitialized = true;
          checkInitialization();
        })
        .catch((error) => {
          console.error('Error fetching UF value:', error);
        });
    } else {
      checkInitialization();
    }
  }, [setDefaultLead]);

  useEffect(() => {
    if (agent) {
      setAgent(agent, assistedSale);
    }
  }, [agent]);

  useEffect(() => quoterPageView(!!agent), []);

  useEffect(() => {
    if (referral) {
      setReferral(referral);
    }
  }, [referral]);

  return {
    setSelectedPlan,
    selectedPlan,
    setNoInsuredMembers,
    noInsuredMembers,
    handleBack,
    handleContinue,
    loading,
    setLoading,
    preLoading,
    setPreLoading,
    dataIsValid,
    validateData,
    setDataIsValid,
    isFlowLoading,
  };
};

export default usePageQuoter;
